import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export interface AuditCycleDto {
  id: string;
  company_id: string;
  name: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AuditResultRow {
  id: string;
  cycle_id: string;
  company_id: string;
  asset_id: string;
  result_status: string;
  notes: string | null;
  verified_at: string | null;
  verified_by: string | null;
  created_at?: string;
  updated_at?: string;
  asset_code: string | null;
  asset_name: string | null;
  asset_status: string | null;
}

export type AuditReportSummaryRow = {
  cycle_id: string;
  name: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  notes: string | null;
  pending: number;
  found: number;
  missing: number;
  damaged: number;
  total: number;
  discrepancy_count: number;
  completion_pct: number;
};

const assetAuditService = {
  async getReportSummary(): Promise<AuditReportSummaryRow[]> {
    const res = await api.get('/asset-audit/report-summary');
    const data = unwrap<AuditReportSummaryRow[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async listCycles(): Promise<AuditCycleDto[]> {
    const res = await api.get('/asset-audit/cycles');
    const data = unwrap<AuditCycleDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getCycle(id: string): Promise<AuditCycleDto> {
    const res = await api.get(`/asset-audit/cycles/${id}`);
    return unwrap(res);
  },
  async createCycle(body: Record<string, unknown>): Promise<AuditCycleDto> {
    const res = await api.post('/asset-audit/cycles', body);
    return unwrap(res);
  },
  async updateCycle(id: string, body: Record<string, unknown>): Promise<AuditCycleDto> {
    const res = await api.put(`/asset-audit/cycles/${id}`, body);
    return unwrap(res);
  },
  async removeCycle(id: string): Promise<void> {
    await api.delete(`/asset-audit/cycles/${id}`);
  },
  async seedAllAssets(cycleId: string): Promise<{ added: number; total_eligible: number }> {
    const res = await api.post(`/asset-audit/cycles/${cycleId}/seed`, {});
    return unwrap(res);
  },
  async addAssetLines(cycleId: string, body: { asset_ids: string[] }): Promise<{ added: number }> {
    const res = await api.post(`/asset-audit/cycles/${cycleId}/lines`, body);
    return unwrap(res);
  },
  async listResults(cycleId: string, status?: string): Promise<AuditResultRow[]> {
    const res = await api.get(`/asset-audit/cycles/${cycleId}/results`, { params: status ? { status } : undefined });
    const data = unwrap<AuditResultRow[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async updateResult(
    resultId: string,
    body: { result_status: string; notes?: string | null }
  ): Promise<AuditResultRow> {
    const res = await api.put(`/asset-audit/results/${resultId}`, body);
    return unwrap(res);
  },

  /** Download CSV of missing + damaged lines for a cycle (UTF-8 BOM, opens in Excel). */
  async downloadDiscrepancyReport(cycleId: string, suggestedFilename: string) {
    const res = await api.getClient().get(`/asset-audit/cycles/${cycleId}/export-discrepancies`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestedFilename || `audit-discrepancies-${cycleId.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export default assetAuditService;
